// Sentry init must run before other imports so its instrumentation can hook
// into fetch/router before any module that uses them. No-op when
// VITE_SENTRY_DSN is unset (dev / preview builds without a DSN stay silent).
import { initSentry } from './lib/sentry';
initSentry();

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from './services/clerkReact';
import { Analytics } from '@vercel/analytics/react'; // <-- Vercel Analytics import added
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// The corrected, split imports
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import App from './App.tsx';
import AuthSync from './components/AuthSync';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from './components/ui/toaster';
import { TooltipProvider } from './components/ui/tooltip';
import './index.css';

// Retrieve the Publishable Keys from the environment variables.
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const APP_URL = import.meta.env.VITE_APP_URL;
const LANDING_URL = import.meta.env.VITE_LANDING_URL;

if (!CLERK_PUBLISHABLE_KEY) {
  console.warn('Clerk publishable key is not configured. Authentication features are disabled for this build.');
}

if (!STRIPE_PUBLISHABLE_KEY) {
  console.warn('Stripe publishable key is not configured. Payments components will be disabled for this build.');
}

// VITE_APP_URL and VITE_LANDING_URL are optional
// If not set, App.tsx will fall back to path-based routing detection
const normalizeOrigin = (value: string | undefined) => value?.replace(/\/+$/, '') ?? '';
const appOrigin = normalizeOrigin(APP_URL) || window.location.origin;
// Auth URLs are anchored to the app domain so Clerk's UI loads on the
// authorised domain. LANDING_URL is still read by App.tsx for routing.
void LANDING_URL;
const resolvedClerkPublishableKey = CLERK_PUBLISHABLE_KEY ?? 'pk_test_placeholder';

// Initialize Stripe outside of the component render to avoid recreating it on every render.
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Configure React Query with optimal caching strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // How long data stays fresh before refetch (5 minutes)
      staleTime: 5 * 60 * 1000,

      // How long inactive data stays in cache (10 minutes)
      gcTime: 10 * 60 * 1000, // Previously cacheTime in v4

      // Don't refetch on window focus for financial data (prevents unnecessary API calls)
      refetchOnWindowFocus: false,

      // Retry failed requests with exponential backoff
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client fault)
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry up to 3 times for 5xx errors (server fault)
        return failureCount < 3;
      },

      // Exponential backoff: 1s, 2s, 4s, max 30s
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      retryDelay: 1000,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <ClerkProvider
          publishableKey={resolvedClerkPublishableKey}
          signInUrl={`${appOrigin}/sign-in`}
          signUpUrl={`${appOrigin}/sign-up`}
          afterSignInUrl={`${appOrigin}/home`}
          afterSignUpUrl={`${appOrigin}/home`}
        >
          <QueryClientProvider client={queryClient}>
            <TooltipProvider delayDuration={200} skipDelayDuration={300}>
              <React.Suspense fallback={null}>
                <AuthSync />
              </React.Suspense>
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <App />
                  <Analytics /> {/* <-- Vercel Analytics component added */}
                </Elements>
              ) : (
                <>
                  <App />
                  <Analytics />
                </>
              )}
              <Toaster />
            </TooltipProvider>
          </QueryClientProvider>
        </ClerkProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
